/**
 * The keyboard: "/" searches, "?" lists the keys, "g" then a letter goes to a page, and a record
 * page can take single letters of its own ("n" for a note, "d" to dial) through useRecordKey.
 * Nothing fires while someone is typing in a field or holding a modifier.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";

export const GO_TO: readonly (readonly [string, string, string])[] = [
  ["h", "/", "Home"],
  ["a", "/tasks", "Agenda"],
  ["b", "/pipeline", "Pipeline board"],
  ["l", "/leads", "Leads"],
  ["c", "/contacts", "Customers"],
  ["p", "/policies", "Policies"],
  ["d", "/dashboards", "Dashboards"],
];

export const RECORD_KEYS: readonly (readonly [string, string])[] = [
  ["d", "Dial the record's number on the desk"],
  ["n", "Write a note"],
  ["w", "Fill in the call form (a lead)"],
];

function typing(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return (
    t.isContentEditable ||
    t.tagName === "INPUT" ||
    t.tagName === "TEXTAREA" ||
    t.tagName === "SELECT" ||
    Boolean(t.closest("[role=dialog]"))
  );
}

const RECORD_EVENT = "tlcrm:record-key";

export function useShortcuts({ search, help }: { search: () => void; help: () => void }) {
  const navigate = useNavigate();
  const pendingG = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e)) return;
      const key = e.key.toLowerCase();
      if (Date.now() - pendingG.current < 1200) {
        pendingG.current = 0;
        const dest = GO_TO.find(([k]) => k === key);
        if (dest) {
          e.preventDefault();
          void navigate(dest[1]);
        }
        return;
      }
      if (key === "g") {
        pendingG.current = Date.now();
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        search();
      } else if (e.key === "?") {
        e.preventDefault();
        help();
      } else if (RECORD_KEYS.some(([k]) => k === key)) {
        window.dispatchEvent(new CustomEvent(RECORD_EVENT, { detail: key }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, search, help]);
}

/** A record page's own single-letter keys, live while the page is mounted. */
export function useRecordKey(handlers: Partial<Record<"d" | "n" | "w", () => void>>) {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  });
  useEffect(() => {
    const on = (e: Event) => {
      const key = (e as CustomEvent<string>).detail as "d" | "n" | "w";
      ref.current[key]?.();
    };
    window.addEventListener(RECORD_EVENT, on);
    return () => window.removeEventListener(RECORD_EVENT, on);
  }, []);
}
